import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronRight, Clock, Copy,
  EllipsisVertical, EyeOff, History, Info, KeyRound, Lock, LogOut, Pencil, Plus, Power,
  RefreshCw, Search, Tablet, Trash2, Users, WifiOff, X, XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import config from '../../configLoader';
import { useSiteContext } from '../../SiteContext';
import ConfirmModal from '../../components/ConfirmModal';
import { PageHead, Banner, EmptyState } from './workOrders/woShared';
import { can } from '../../util/adminPermissions';
import * as KioskAPI from '../../api/kioskDevices';
import { KioskDevice, KioskEnrollmentCode } from '../../api/kioskDevices';
import {
  CODE_LOW_MS, FILTER_LABEL, NAME_MAX, NOTE_MAX, StatusFilter,
  absSeen, codeDigits, codeGroups, codeMsLeft, codeSuperseded, countStatuses, enrolledSince,
  hasLiveCode, isFresh, isStale, longDate, mmss, nameTakenMessage, punchesLabel, relTime,
  shortDate, clockTime, spokenCode, validateDeviceName, visibleDevices,
} from './kioskDeviceHelpers';
import './KioskDevicesPage.css';

/**
 * Kiosk Devices — Setup › Kiosk Devices (TAS-585).
 *
 * Every kiosk tablet is its own admin-managed device. An admin adds it here,
 * generates a 6-digit one-time code, and reads it to whoever is at the tablet;
 * the tablet exchanges the code for its own Firebase identity. Employees still
 * sign in on the tablet with their PIN.
 *
 * Built from docs/featureBriefs/design_handoff_kiosk_devices against the API
 * contract in API/docs/featureBriefs/KioskDeviceEnrollment_02_ApiContract.md.
 * Where the two differ the contract wins: `/admin/kioskDevices`, and deactivate /
 * reactivate are `PATCH {active}` rather than their own endpoints.
 *
 * Product decisions (handoff "Confirmed decisions") this page must keep:
 *  D1 Generating a RE-ENROLL code does not sign out the current tablet — only a new
 *     tablet redeeming it does. The copy says so; nothing here signs anything out.
 *  D2 Reactivate brings a device back as Not enrolled.
 *  D3 The code screen never closes on a backdrop click: the code can't be shown again.
 *  D4 The table shows only the countdown for a pending code, never the code. The
 *     code lives in this component's state for as long as the modal is open, and
 *     nowhere else.
 *
 * Each device is pinned to one KioskApp company ID (NPM serves "npm" and "b2c"), and
 * /kiosk/enroll rejects a tablet that chose a different one — so the code screen
 * tells the person at the tablet exactly which ID to enter.
 */

const POLL_MS = 3000;
const TOAST_MS = 3600;
const FLASH_MS = 1600;

/** The company IDs this tenant's tablets can enroll under (config, never the API). */
const KIOSK_COMPANY_IDS: string[] = config.kioskCompanyIDs ?? [];

type CodeState = 'active' | 'expired' | 'success';
type ExpiredReason = 'timeout' | 'replaced' | 'deactivated';

interface CodeSession {
  deviceID: number;
  code: KioskEnrollmentCode;
  /** Local-clock deadline: receipt time + the server's TTL, so client clock skew
   *  can't make the countdown lie. */
  deadline: number;
  ttl: number;
  reenroll: boolean;
  /** A live code existed when this one was generated (it has now been cancelled). */
  replaced: boolean;
  state: CodeState;
  expiredReason?: ExpiredReason;
}

type ModalState =
  | { kind: 'edit'; device: KioskDevice | null }
  | { kind: 'added'; device: KioskDevice }
  | { kind: 'reenroll'; device: KioskDevice }
  | { kind: 'deactivate'; device: KioskDevice }
  | { kind: 'reactivate'; device: KioskDevice }
  | { kind: 'delete'; device: KioskDevice };

type MenuState =
  | { kind: 'row'; device: KioskDevice; anchor: DOMRect }
  | { kind: 'status'; anchor: DOMRect };

interface Toast { id: number; msg: string; bad: boolean }

/* ====================================================================== *
 * Modal shell — the handoff's 480 / 580 px dialog. ConfirmModal covers the
 * two-button confirms; this one is for the forms, the code screen and the
 * three-button reactivate.
 * ====================================================================== */

const KdModal: React.FC<{
  tone?: '' | 'warn' | 'deny' | 'ok';
  Icon?: LucideIcon;
  title?: React.ReactNode;
  sub?: React.ReactNode;
  wide?: boolean;
  /** false = backdrop clicks do nothing (the code screen, D3). Esc and × still close. */
  backdropCloses?: boolean;
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
}> = ({ tone = '', Icon, title, sub, wide, backdropCloses = true, onClose, footer, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="kd-modal-bg"
      onMouseDown={(e) => { if (backdropCloses && e.target === e.currentTarget) onClose(); }}
    >
      <div className={`kd-modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true">
        <div className={`kd-mhead ${tone}`}>
          {Icon && <span className="mi"><Icon /></span>}
          <div className="grow">
            {title && <h3>{title}</h3>}
            {sub && <div className="ms">{sub}</div>}
          </div>
          <button type="button" className="mclose" onClick={onClose} aria-label="Close"><X /></button>
        </div>
        <div className="kd-mbody">{children}</div>
        <div className="kd-mfoot">{footer}</div>
      </div>
    </div>
  );
};

/* ====================================================================== *
 * Float menu — fixed-position, anchored to its trigger, flips up near the
 * bottom of the viewport. Closed by the page on outside click / scroll / Esc.
 * ====================================================================== */

const FloatMenu: React.FC<{
  anchor: DOMRect;
  align: 'left' | 'right';
  minWidth?: number;
  children: React.ReactNode;
}> = ({ anchor, align, minWidth, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let top = anchor.bottom + 6;
    if (top + h > window.innerHeight - 10) top = Math.max(10, anchor.top - h - 6);
    const want = align === 'right' ? anchor.right - w : anchor.left;
    const left = Math.max(10, Math.min(window.innerWidth - w - 10, want));
    setPos({ top, left });
  }, [anchor, align]);

  return (
    <div
      ref={ref}
      className="kd-floatmenu"
      data-kd-menu
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, minWidth }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
};

/* ====================================================================== *
 * The page
 * ====================================================================== */

const KioskDevicesPage: React.FC = () => {
  const { userRole, userName } = useSiteContext();
  const allowed = can(userRole, 'manageKioskDevices');

  const [devices, setDevices] = useState<KioskDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [codeSession, setCodeSession] = useState<CodeSession | null>(null);
  const [generatingFor, setGeneratingFor] = useState<number | null>(null);
  const [flashID, setFlashID] = useState<number | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [now, setNow] = useState(() => Date.now());

  /* ---------------- toasts / flash ---------------- */

  const toastSeq = useRef(0);
  const showToast = useCallback((msg: string, bad = false) => {
    const id = ++toastSeq.current;
    setToasts((prev) => [...prev, { id, msg, bad }]);
    window.setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), TOAST_MS);
  }, []);

  const flashTimer = useRef<number | undefined>(undefined);
  const flash = useCallback((id: number) => {
    setFlashID(null);
    window.clearTimeout(flashTimer.current);
    // Next frame, so re-flashing the same row restarts the animation.
    window.requestAnimationFrame(() => {
      setFlashID(id);
      flashTimer.current = window.setTimeout(() => setFlashID(null), FLASH_MS);
    });
  }, []);
  useEffect(() => () => window.clearTimeout(flashTimer.current), []);

  /* ---------------- data ---------------- */

  const loadSeq = useRef(0);
  const loadDevices = useCallback(async (quiet = false) => {
    const seq = ++loadSeq.current;
    if (!quiet) setLoading(true);
    setLoadError(null);
    try {
      const rows = await KioskAPI.getKioskDevices();
      if (seq === loadSeq.current) setDevices(rows);
    } catch (err) {
      if (seq === loadSeq.current) {
        setLoadError(KioskAPI.getKioskDeviceErrorMessage(err, "Couldn't load kiosk devices."));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { if (allowed) loadDevices(); }, [allowed, loadDevices]);

  /** Replace one device in the list (or append it) without a full reload. */
  const upsert = useCallback((d: KioskDevice) => {
    setDevices((prev) => {
      const i = prev.findIndex((x) => x.kioskDeviceID === d.kioskDeviceID);
      if (i === -1) return [...prev, d];
      const next = [...prev];
      next[i] = d;
      return next;
    });
  }, []);

  const byID = useMemo(() => {
    const m = new Map<number, KioskDevice>();
    for (const d of devices) m.set(d.kioskDeviceID, d);
    return m;
  }, [devices]);

  /* ---------------- clock ---------------- */

  // 1 s while anything is counting down (a live code in the table or the code screen),
  // otherwise 30 s — the handoff's refresh rate for relative "last seen" times.
  const fastTick = codeSession?.state === 'active' || devices.some((d) => hasLiveCode(d, now));
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), fastTick ? 1000 : 30000);
    return () => window.clearInterval(id);
  }, [fastTick]);

  /* ---------------- menus ---------------- */

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menu]);

  const toggleRowMenu = (e: React.MouseEvent<HTMLButtonElement>, d: KioskDevice) => {
    e.stopPropagation();
    const anchor = e.currentTarget.getBoundingClientRect();
    setMenu((m) => (m?.kind === 'row' && m.device.kioskDeviceID === d.kioskDeviceID ? null : { kind: 'row', device: d, anchor }));
  };

  /* ---------------- enrollment code ---------------- */

  const openCode = useCallback(async (d: KioskDevice, reenroll: boolean) => {
    if (generatingFor != null) return;
    const replaced = hasLiveCode(d, Date.now());
    setGeneratingFor(d.kioskDeviceID);
    try {
      const code = await KioskAPI.generateKioskEnrollmentCode(d.kioskDeviceID);
      const received = Date.now();
      const issued = Date.parse(code.issuedAt);
      const expires = Date.parse(code.expiresAt);
      const ttl = Number.isFinite(issued) && Number.isFinite(expires) && expires > issued
        ? expires - issued
        : 15 * 60_000;
      setModal(null);
      setCodeSession({
        deviceID: d.kioskDeviceID,
        code,
        deadline: received + ttl,
        ttl,
        reenroll: reenroll || d.status === 'active' || code.purpose === 'reenroll',
        replaced,
        state: 'active',
      });
      setNow(received);
      // Reflect the new pending code in the row right away; the poll keeps it fresh.
      upsert({ ...d, pendingCode: { issuedAt: code.issuedAt, expiresAt: code.expiresAt, purpose: code.purpose } });
    } catch (err) {
      showToast(KioskAPI.getKioskDeviceErrorMessage(err, "Couldn't generate a code. Try again."), true);
      if (KioskAPI.getKioskDeviceErrorStatus(err) === 409) loadDevices(true);
    } finally {
      setGeneratingFor(null);
    }
  }, [generatingFor, upsert, showToast, loadDevices]);

  // Expire on the local deadline.
  useEffect(() => {
    if (codeSession?.state === 'active' && now >= codeSession.deadline) {
      setCodeSession((s) => (s && s.state === 'active' ? { ...s, state: 'expired', expiredReason: 'timeout' } : s));
    }
  }, [now, codeSession]);

  // Poll GET /:id every 3 s while a code is live; flip to success when the device
  // shows an enrollment at or after this code's issuedAt.
  const pollingDeviceID = codeSession?.state === 'active' ? codeSession.deviceID : null;
  const pollingIssuedAt = codeSession?.state === 'active' ? codeSession.code.issuedAt : null;
  useEffect(() => {
    if (pollingDeviceID == null || pollingIssuedAt == null) return;
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const d = await KioskAPI.getKioskDevice(pollingDeviceID);
        if (cancelled) return;
        upsert(d);
        if (enrolledSince(d, pollingIssuedAt)) {
          setCodeSession((s) => (s && s.deviceID === d.kioskDeviceID ? { ...s, state: 'success' } : s));
          flash(d.kioskDeviceID);
        } else if (d.status === 'deactivated') {
          setCodeSession((s) => (s && s.state === 'active' ? { ...s, state: 'expired', expiredReason: 'deactivated' } : s));
        } else if (codeSuperseded(d, pollingIssuedAt)) {
          setCodeSession((s) => (s && s.state === 'active' ? { ...s, state: 'expired', expiredReason: 'replaced' } : s));
        }
      } catch (err) {
        if (cancelled) return;
        if (KioskAPI.getKioskDeviceErrorStatus(err) === 404) {
          setCodeSession(null);
          showToast('That device no longer exists.', true);
          loadDevices(true);
        }
        // Anything else is transient — the next tick retries.
      } finally {
        inFlight = false;
      }
    };
    const id = window.setInterval(tick, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [pollingDeviceID, pollingIssuedAt, upsert, flash, showToast, loadDevices]);

  const closeCode = useCallback(() => {
    setCodeSession(null);
    loadDevices(true);
  }, [loadDevices]);

  /* ---------------- row actions ---------------- */

  const act = useCallback((action: string, d: KioskDevice) => {
    setMenu(null);
    switch (action) {
      case 'code':
        if (d.status === 'active') setModal({ kind: 'reenroll', device: d });
        else openCode(d, false);
        break;
      case 'reenroll': setModal({ kind: 'reenroll', device: d }); break;
      case 'edit': setModal({ kind: 'edit', device: d }); break;
      case 'deactivate': setModal({ kind: 'deactivate', device: d }); break;
      case 'reactivate': setModal({ kind: 'reactivate', device: d }); break;
      case 'delete': setModal({ kind: 'delete', device: d }); break;
    }
  }, [openCode]);

  const deactivate = useCallback(async (d: KioskDevice) => {
    try {
      const updated = await KioskAPI.setKioskDeviceActive(d.kioskDeviceID, false);
      upsert(updated);
      setModal(null);
      flash(d.kioskDeviceID);
      showToast(`Deactivated · ${d.name}${d.status === 'active' ? ' — the tablet signs out on its next request' : ''}`);
    } catch (err) {
      setModal(null);
      showToast(KioskAPI.getKioskDeviceErrorMessage(err, "Couldn't deactivate the device. Try again."), true);
    }
  }, [upsert, flash, showToast]);

  const reactivate = useCallback(async (d: KioskDevice, thenCode: boolean) => {
    try {
      const updated = await KioskAPI.setKioskDeviceActive(d.kioskDeviceID, true);
      upsert(updated);
      flash(d.kioskDeviceID);
      if (thenCode) {
        await openCode(updated, false);
      } else {
        setModal(null);
        showToast(`Reactivated · ${d.name} is ready to enroll`);
      }
    } catch (err) {
      setModal(null);
      const status = KioskAPI.getKioskDeviceErrorStatus(err);
      showToast(
        status === 409 && KioskAPI.getKioskDeviceErrorField(err) === 'name'
          ? `Couldn't reactivate — ${nameTakenMessage(d.name)} Rename one of them first.`
          : KioskAPI.getKioskDeviceErrorMessage(err, "Couldn't reactivate the device. Try again."),
        true,
      );
    }
  }, [upsert, flash, openCode, showToast]);

  const remove = useCallback(async (d: KioskDevice) => {
    try {
      await KioskAPI.deleteKioskDevice(d.kioskDeviceID);
      setDevices((prev) => prev.filter((x) => x.kioskDeviceID !== d.kioskDeviceID));
      setModal(null);
      showToast(`Deleted · ${d.name}`);
    } catch (err) {
      setModal(null);
      showToast(KioskAPI.getKioskDeviceErrorMessage(err, "Couldn't delete the device. Try again."), true);
      if (KioskAPI.getKioskDeviceErrorStatus(err) === 409) loadDevices(true);
    }
  }, [showToast, loadDevices]);

  /* ---------------- gates ---------------- */

  // Defensive: App.tsx already sends roles without ManageKioskDevices to Home.
  if (!allowed) {
    return (
      <div className="kd">
        <EmptyState Icon={Lock} title="Not authorized" style={{ marginTop: 40 }}>
          The Kiosk Devices page requires the Manage kiosk devices permission. Ask an
          administrator if you need access.
        </EmptyState>
      </div>
    );
  }

  /* ---------------- derived ---------------- */

  const counts = countStatuses(devices, now);
  const rows = visibleDevices(devices, filter, query, now);
  const filtered = filter !== 'all' || query.trim() !== '';
  const isEmpty = !loading && !loadError && devices.length === 0;
  const noCompany = KIOSK_COMPANY_IDS.length === 0;
  const multiCompany = KIOSK_COMPANY_IDS.length > 1;
  const clearFilters = () => { setFilter('all'); setQuery(''); };
  const openAdd = () => setModal({ kind: 'edit', device: null });

  /* ---------------- cells ---------------- */

  const statusCell = (d: KioskDevice) => {
    const live = hasLiveCode(d, now);
    if (d.status === 'active') {
      return (
        <>
          <span className="kd-pill active"><span className="dot" />Active</span>
          {live && (
            <div className="ssub live"><KeyRound />Re-enroll code · <span className="mono">{mmss(codeMsLeft(d, now))}</span></div>
          )}
        </>
      );
    }
    if (d.status === 'not_enrolled') {
      return (
        <>
          <span className="kd-pill pending"><span className="dot" />Not enrolled</span>
          {live
            ? <div className="ssub live"><Clock />Code expires in <span className="mono">{mmss(codeMsLeft(d, now))}</span></div>
            : d.pendingCode
              ? <div className="ssub"><XCircle />Code expired, not used</div>
              : <div className="ssub">No code generated yet</div>}
        </>
      );
    }
    return (
      <>
        <span className="kd-pill off"><span className="dot" />Deactivated</span>
        <div className="ssub">
          {shortDate(d.deactivatedAt)}{d.deactivatedBy ? ` · ${d.deactivatedBy.name}` : ''}
        </div>
      </>
    );
  };

  const seenCell = (d: KioskDevice) => {
    if (!d.lastSeenAt) {
      return <div className="seen never">Never<span className="abs">{d.status === 'active' ? 'Not checked in yet' : 'No tablet yet'}</span></div>;
    }
    if (isStale(d, now)) {
      return (
        <div className="seen stale" title="Not seen in over 24 hours">
          <span className="sv"><AlertTriangle />{relTime(d.lastSeenAt, now)}</span>
          <span className="abs">Check the tablet is on and online</span>
        </div>
      );
    }
    return (
      <div className={`seen${d.status === 'deactivated' ? ' never' : ''}`}>
        {isFresh(d, now) && <span className="live-dot" />}
        {relTime(d.lastSeenAt, now)}
        <span className="abs">{absSeen(d.lastSeenAt, now)}</span>
      </div>
    );
  };

  const createdLine = (d: KioskDevice) =>
    `Created ${shortDate(d.createdAt)}${d.createdBy ? ` by ${d.createdBy.name}` : ''}`;

  const actCell = (d: KioskDevice) => {
    const busy = generatingFor === d.kioskDeviceID;
    const menuOpen = menu?.kind === 'row' && menu.device.kioskDeviceID === d.kioskDeviceID;
    return (
      <div className="acts">
        {d.status === 'not_enrolled' && (
          <button type="button" className="kd-btn sm" disabled={busy} onClick={() => act('code', d)}>
            <KeyRound />{busy ? 'Generating…' : d.pendingCode ? 'New code' : 'Generate code'}
          </button>
        )}
        {d.status === 'deactivated' && (
          <button type="button" className="kd-btn sm" onClick={() => act('reactivate', d)}>
            <Power />Reactivate
          </button>
        )}
        <button
          type="button"
          className={`kebab${menuOpen ? ' open' : ''}`}
          title="More actions"
          aria-label={`More actions for ${d.name}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => toggleRowMenu(e, d)}
        >
          <EllipsisVertical />
        </button>
      </div>
    );
  };

  /* ---------------- render pieces ---------------- */

  const renderRowMenu = (d: KioskDevice, anchor: DOMRect) => {
    const live = hasLiveCode(d, now);
    type Item = { a: string; Icon: LucideIcon; label: string; sub?: string; danger?: boolean } | 'sep';
    const items: Item[] = [];
    if (d.status === 'active') items.push({ a: 'reenroll', Icon: RefreshCw, label: 'Re-enroll', sub: 'Signs out the current tablet' });
    if (d.status === 'not_enrolled') {
      items.push({
        a: 'code', Icon: KeyRound,
        label: d.pendingCode ? 'Generate new code' : 'Generate enrollment code',
        sub: live ? 'Cancels the pending code' : undefined,
      });
    }
    items.push({ a: 'edit', Icon: Pencil, label: 'Edit name & note' });
    items.push('sep');
    if (d.status === 'deactivated') items.push({ a: 'reactivate', Icon: Power, label: 'Reactivate' });
    else items.push({ a: 'deactivate', Icon: Power, label: 'Deactivate', danger: true });
    if (!d.everEnrolled) {
      items.push({ a: 'delete', Icon: Trash2, label: 'Delete', sub: 'Never enrolled, so nothing references it', danger: true });
    }
    return (
      <FloatMenu anchor={anchor} align="right" minWidth={240}>
        {items.map((it, i) => it === 'sep'
          ? <div className="fm-sep" key={`sep${i}`} />
          : (
            <button type="button" key={it.a} className={`fm-opt${it.danger ? ' danger' : ''}`} onClick={() => act(it.a, d)}>
              <it.Icon />
              <span>{it.label}{it.sub && <span className="fm-sub">{it.sub}</span>}</span>
            </button>
          ))}
      </FloatMenu>
    );
  };

  const renderStatusMenu = (anchor: DOMRect) => {
    const n: Record<StatusFilter, number> = {
      all: devices.length, active: counts.active, stale: counts.stale,
      not_enrolled: counts.not_enrolled, deactivated: counts.deactivated,
    };
    return (
      <FloatMenu anchor={anchor} align="left">
        {(Object.keys(FILTER_LABEL) as StatusFilter[]).map((k) => (
          <button
            type="button"
            key={k}
            className={`fm-opt${filter === k ? ' sel' : ''}`}
            onClick={() => { setFilter(k); setMenu(null); }}
          >
            {FILTER_LABEL[k]}<span className="fm-cnt">{n[k]}</span>
          </button>
        ))}
      </FloatMenu>
    );
  };

  const kpi = (key: Exclude<StatusFilter, 'all'>, cls: string, Icon: LucideIcon, label: string, value: number, sub: string) => (
    <button
      type="button"
      className={`kpi ${cls}${filter === key ? ' on' : ''}`}
      onClick={() => setFilter((f) => (f === key ? 'all' : key))}
      aria-pressed={filter === key}
    >
      <div className="kl"><Icon />{label}</div>
      <div className={`kv${value ? '' : ' zero'}`}>{value}</div>
      <div className="ks">{sub}</div>
    </button>
  );

  const renderList = () => (
    <>
      <div className="kpis">
        {kpi('active', 'k-active', CheckCircle2, 'Active', counts.active, 'enrolled tablets')}
        {kpi('not_enrolled', 'k-pending', KeyRound, 'Not enrolled', counts.not_enrolled, 'waiting for a tablet')}
        {kpi('deactivated', 'k-off', Power, 'Deactivated', counts.deactivated, "signed out, can't punch")}
        {kpi('stale', 'k-stale', WifiOff, 'Not seen 24h+', counts.stale,
          counts.stale === 0 ? 'every active kiosk checked in'
            : counts.stale === 1 ? 'active device to check' : 'active devices to check')}
      </div>

      <div className="filterbar">
        <div className="searchwrap">
          <Search />
          <input
            type="text"
            value={query}
            placeholder="Search device name or note…"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button
          type="button"
          className={`fsel${filter !== 'all' ? ' active' : ''}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            const anchor = e.currentTarget.getBoundingClientRect();
            setMenu((m) => (m?.kind === 'status' ? null : { kind: 'status', anchor }));
          }}
        >
          <span className="fs-lab">Status</span>
          <span className={`fs-val${filter === 'all' ? ' placeholder' : ''}`}>{FILTER_LABEL[filter]}</span>
          <ChevronDown className="caret" />
        </button>
        {filtered && (
          <button type="button" className="clear-btn" onClick={clearFilters}><XCircle />Clear</button>
        )}
      </div>

      <div className="resultline"><span><b>{rows.length}</b> of {devices.length} devices</span></div>

      {rows.length > 0 ? (
        <div className="table-card">
          <table className="xt">
            <thead>
              <tr>
                <th>Device</th>
                <th>Status</th>
                <th>Last seen</th>
                <th>Enrolled</th>
                <th className="num">Punches today</th>
                <th className="col-act"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.kioskDeviceID}
                  className={[
                    d.status === 'deactivated' ? 'off-row' : '',
                    flashID === d.kioskDeviceID ? 'flash' : '',
                  ].join(' ').trim() || undefined}
                >
                  <td>
                    <div className="dev">
                      <div className="dn">
                        <Tablet />{d.name}
                        {multiCompany && <span className="co" title="Company ID the tablet enrolls under">{d.companyID}</span>}
                      </div>
                      <div className={`dnote${d.note ? '' : ' empty'}`}>{d.note || 'No note'}</div>
                    </div>
                  </td>
                  <td>{statusCell(d)}</td>
                  <td>{seenCell(d)}</td>
                  <td>
                    <div className="enr">
                      {d.enrolledAt ? longDate(d.enrolledAt) : <span className="muted">—</span>}
                      <span className="by">{createdLine(d)}</span>
                    </div>
                  </td>
                  <td className="num">
                    {d.status === 'not_enrolled'
                      ? <span className="pz">—</span>
                      : d.punchesToday ? d.punchesToday : <span className="pz">0</span>}
                  </td>
                  <td className="col-act">{actCell(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="state zero">
          <div className="st-ic"><Search /></div>
          <h3>No devices match</h3>
          <p>Try a different search or clear the status filter.</p>
          <div className="st-act">
            <button type="button" className="kd-btn" onClick={clearFilters}><XCircle />Clear filters</button>
          </div>
        </div>
      )}
    </>
  );

  const renderIntro = () => (
    <div className="state intro">
      <div className="st-ic"><Tablet /></div>
      <h3>No kiosk devices yet</h3>
      <p>
        A kiosk device is a shared tablet where crews punch in and out with their own PIN. It's set
        up once by you — not signed in as an employee — so it keeps working no matter whose phone or
        role changes.
      </p>
      <div className="howto">
        <div className="hs"><span className="hn">1</span><h4>Add a device</h4><p>Name it after where it lives, like “Indio Warehouse”.</p></div>
        <div className="hs"><span className="hn">2</span><h4>Read the code</h4><p>Generate a 6-digit code and read it to the person at the tablet.</p></div>
        <div className="hs"><span className="hn">3</span><h4>Crews punch</h4><p>Employees sign in with their PIN. Each punch is stamped with the device.</p></div>
      </div>
      <div className="st-act" style={{ marginTop: 24 }}>
        <button type="button" className="kd-btn primary" onClick={openAdd} disabled={noCompany}><Plus />Add device</button>
      </div>
    </div>
  );

  /* ---------------- shell ---------------- */

  const codeDevice = codeSession ? byID.get(codeSession.deviceID) ?? null : null;

  return (
    <div className="kd">
      <div className="kd-crumbs"><b>Setup</b><ChevronRight /><span>Kiosk Devices</span></div>

      <PageHead
        Icon={Tablet}
        title="Kiosk Devices"
        sub="Shared tablets running the Task & Time Kiosk app. Each tablet is its own device, enrolled once with a one-time code. Employees still sign in on it with their own PIN."
        actions={!isEmpty && !loading && !loadError ? (
          <div className="ph-right">
            <button type="button" className="kd-btn primary" onClick={openAdd} disabled={noCompany}><Plus />Add device</button>
          </div>
        ) : undefined}
      />

      {noCompany && (
        <Banner variant="warn" Icon={AlertTriangle}>
          <b>No kiosk company ID is configured for this site</b>, so new devices can't be added. Set
          <code> kioskCompanyIDs</code> in the AdminApp config to the company ID(s) the Kiosk app uses
          for this tenant.
        </Banner>
      )}

      {loading ? (
        <div className="kd-loading">Loading kiosk devices…</div>
      ) : loadError ? (
        <Banner
          variant="danger"
          Icon={AlertTriangle}
          action={<button type="button" className="kd-btn sm" onClick={() => loadDevices()}><RefreshCw />Retry</button>}
        >
          {loadError}
        </Banner>
      ) : isEmpty ? renderIntro() : renderList()}

      {menu?.kind === 'row' && renderRowMenu(byID.get(menu.device.kioskDeviceID) ?? menu.device, menu.anchor)}
      {menu?.kind === 'status' && renderStatusMenu(menu.anchor)}

      {/* ---------------- add / edit ---------------- */}
      {modal?.kind === 'edit' && (
        <DeviceForm
          device={modal.device}
          devices={devices}
          multiCompany={multiCompany}
          onClose={() => setModal(null)}
          onSaved={(saved, isNew) => {
            upsert(saved);
            flash(saved.kioskDeviceID);
            if (isNew) {
              setModal({ kind: 'added', device: saved });
            } else {
              setModal(null);
              showToast(`Saved · ${saved.name}`);
            }
          }}
        />
      )}

      {modal?.kind === 'added' && (
        <KdModal
          tone="ok"
          Icon={CheckCircle2}
          title="Device added"
          sub={modal.device.name}
          onClose={() => setModal(null)}
          footer={(
            <>
              <button type="button" className="kd-btn" onClick={() => setModal(null)}>Later</button>
              <button
                type="button"
                className="kd-btn primary"
                autoFocus
                disabled={generatingFor != null}
                onClick={() => openCode(byID.get(modal.device.kioskDeviceID) ?? modal.device, false)}
              >
                <KeyRound />{generatingFor != null ? 'Generating…' : 'Generate enrollment code now'}
              </button>
            </>
          )}
        >
          <p className="lead">
            Next, enroll the tablet. You'll get a 6-digit code to read to whoever is at the tablet — it
            works for 15 minutes.
          </p>
          <div className="note-strip info">
            <Info />
            <span>
              Not at the phone with them yet? Choose <b>Later</b> — the device waits as <b>Not enrolled</b> and
              you can generate a code from its row any time.
            </span>
          </div>
        </KdModal>
      )}

      {/* ---------------- confirms ---------------- */}
      {modal?.kind === 'reenroll' && (
        <ConfirmModal
          variant="warning"
          icon={<RefreshCw size={20} />}
          title={`Re-enroll ${modal.device.name}?`}
          subtitle="Only when the tablet was replaced, reset, or reinstalled."
          confirmLabel="Generate re-enroll code"
          detail={(
            <div className="kd-facts">
              <div className="f">
                <LogOut />
                <span>
                  <b>The tablet signed in now will be signed out</b> as soon as a tablet enrolls with the new
                  code. Until then it keeps working.
                </span>
              </div>
              <div className="f">
                <Activity />
                <span>
                  It was last seen <b>{modal.device.lastSeenAt ? relTime(modal.device.lastSeenAt, now) : 'never'}</b> with{' '}
                  <b>{punchesLabel(modal.device.punchesToday)}</b> today. If it's working, you probably don't need this.
                </span>
              </div>
              <div className="f"><History /><span>Punches already recorded stay stamped with this device.</span></div>
            </div>
          )}
          onConfirm={() => openCode(modal.device, true)}
          onClose={() => setModal(null)}
        />
      )}

      {modal?.kind === 'deactivate' && (() => {
        const d = modal.device;
        const active = d.status === 'active';
        return (
          <ConfirmModal
            variant="error"
            icon={<Power size={20} />}
            title={`Deactivate ${d.name}?`}
            subtitle={active
              ? `Last seen ${d.lastSeenAt ? relTime(d.lastSeenAt, now) : 'never'} · ${punchesLabel(d.punchesToday)} today`
              : 'Not enrolled'}
            confirmLabel="Deactivate"
            detail={(
              <>
                {active ? (
                  <>
                    <p className="kd-lead">The tablet is signed out on its next request — usually within a minute — and shows:</p>
                    <div className="kd-tablet-msg">
                      <span className="tm-ic"><Tablet /></span>
                      <div>
                        <div className="tm-l">On the tablet</div>
                        <div className="tm-t">This kiosk has been deactivated — contact the office</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="kd-lead">
                    {hasLiveCode(d, now) ? 'Its pending enrollment code stops working right away. ' : ''}
                    No tablet can enroll as this device until it's reactivated.
                  </p>
                )}
                <div className="kd-facts">
                  <div className="f"><Users /><span>Employees can't punch on it. They can still punch on other kiosks or the mobile app.</span></div>
                  <div className="f"><Power /><span>Reactivating later needs a new enrollment code.</span></div>
                </div>
              </>
            )}
            onConfirm={() => deactivate(d)}
            onClose={() => setModal(null)}
          />
        );
      })()}

      {modal?.kind === 'reactivate' && (
        <ReactivateModal
          device={modal.device}
          onCancel={() => setModal(null)}
          onReactivate={(thenCode) => reactivate(modal.device, thenCode)}
        />
      )}

      {modal?.kind === 'delete' && (
        <ConfirmModal
          variant="error"
          icon={<Trash2 size={20} />}
          title={`Delete ${modal.device.name}?`}
          subtitle={createdLine(modal.device)}
          confirmLabel="Delete device"
          detail={(
            <p className="kd-lead">
              It was never enrolled, so no punches reference it.{' '}
              {hasLiveCode(modal.device, now) ? 'Its pending code stops working right away. ' : ''}
              This can't be undone.
            </p>
          )}
          onConfirm={() => remove(modal.device)}
          onClose={() => setModal(null)}
        />
      )}

      {/* ---------------- enrollment code ---------------- */}
      {codeSession && codeDevice && (
        <CodeModal
          session={codeSession}
          device={codeDevice}
          now={now}
          adminName={userName || 'You'}
          generating={generatingFor != null}
          onNewCode={() => openCode(codeDevice, codeSession.reenroll)}
          onClose={closeCode}
        />
      )}

      {toasts.length > 0 && (
        <div className="kd-toasts">
          {toasts.map((t) => (
            <div key={t.id} className={`kd-toast${t.bad ? ' bad' : ''}`}>
              {t.bad ? <AlertTriangle /> : <CheckCircle2 />}<span>{t.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ====================================================================== *
 * Add / edit form
 * ====================================================================== */

const DeviceForm: React.FC<{
  device: KioskDevice | null;
  devices: KioskDevice[];
  multiCompany: boolean;
  onClose: () => void;
  onSaved: (device: KioskDevice, isNew: boolean) => void;
}> = ({ device, devices, multiCompany, onClose, onSaved }) => {
  const isNew = !device;
  const [name, setName] = useState(device?.name ?? '');
  const [note, setNote] = useState(device?.note ?? '');
  const [companyID, setCompanyID] = useState(device?.companyID ?? KIOSK_COMPANY_IDS[0] ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  // The company is part of what the tablet must type to enroll, so it is fixed once a
  // tablet has enrolled (the API 409s a change after that).
  const companyLocked = !!device?.everEnrolled;
  // An existing device pinned to an ID this config no longer lists still shows it.
  const companyOptions = device && !KIOSK_COMPANY_IDS.includes(device.companyID)
    ? [device.companyID, ...KIOSK_COMPANY_IDS]
    : KIOSK_COMPANY_IDS;

  useEffect(() => {
    const id = window.setTimeout(() => { nameRef.current?.focus(); nameRef.current?.select(); }, 30);
    return () => window.clearTimeout(id);
  }, []);

  const save = async () => {
    if (saving) return;
    const err = validateDeviceName(name, devices, device);
    if (err) { setNameError(err); nameRef.current?.focus(); return; }
    const trimmedName = name.trim();
    const trimmedNote = note.trim() || null;
    if (!companyID) { setFormError('Choose the company this tablet enrolls under.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      if (isNew) {
        const created = await KioskAPI.createKioskDevice({ name: trimmedName, note: trimmedNote, companyID });
        onSaved(created, true);
      } else {
        const patch: KioskAPI.KioskDeviceUpdateInput = {};
        if (trimmedName !== device!.name) patch.name = trimmedName;
        if (trimmedNote !== (device!.note ?? null)) patch.note = trimmedNote;
        if (!companyLocked && companyID !== device!.companyID) patch.companyID = companyID;
        if (Object.keys(patch).length === 0) { onClose(); return; }
        const updated = await KioskAPI.updateKioskDevice(device!.kioskDeviceID, patch);
        onSaved(updated, false);
      }
    } catch (e) {
      if (KioskAPI.getKioskDeviceErrorStatus(e) === 409 && KioskAPI.getKioskDeviceErrorField(e) === 'name') {
        setNameError(nameTakenMessage(trimmedName));
        nameRef.current?.focus();
      } else {
        setFormError(KioskAPI.getKioskDeviceErrorMessage(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <KdModal
      Icon={isNew ? Plus : Pencil}
      title={isNew ? 'Add kiosk device' : 'Edit device'}
      sub={isNew ? 'Create the device first — you’ll enroll the tablet with a code next.' : device!.name}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="kd-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="kd-btn primary" onClick={save} disabled={saving}>
            {isNew ? <Plus /> : <Check />}
            {saving ? 'Saving…' : isNew ? 'Add device' : 'Save changes'}
          </button>
        </>
      )}
    >
      <div className={`fld${nameError ? ' err' : ''}`}>
        <label htmlFor="kdName">Name</label>
        <input
          id="kdName"
          ref={nameRef}
          maxLength={NAME_MAX}
          placeholder="e.g. Indio Warehouse, Corona Dock A"
          value={name}
          onChange={(e) => { setName(e.target.value); setNameError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
        />
        {nameError && <div className="ferr"><AlertTriangle /><span>{nameError}</span></div>}
        <div className="hint">Shown on the tablet and stamped on every punch made there. Must be unique.</div>
      </div>

      {multiCompany && (
        <div className="fld">
          <label htmlFor="kdCompany">Company</label>
          <select
            id="kdCompany"
            value={companyID}
            disabled={companyLocked}
            onChange={(e) => setCompanyID(e.target.value)}
          >
            {companyOptions.map((id) => <option key={id} value={id}>{id}</option>)}
          </select>
          <div className="hint">
            {companyLocked
              ? 'Fixed once a tablet has enrolled as this device.'
              : 'The company ID the person at the tablet enters to enroll it.'}
          </div>
        </div>
      )}

      <div className="fld">
        <label htmlFor="kdNote">
          Note <span className="opt">optional</span><span className="cnt">{note.length} / {NOTE_MAX}</span>
        </label>
        <textarea
          id="kdNote"
          rows={3}
          maxLength={NOTE_MAX}
          placeholder="Where it is and who to call — e.g. “Break room wall. Site lead: Maria, (760) 555-0143”"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="hint">Devices aren’t tied to a Location, so use the note for where it lives and who’s on site.</div>
      </div>

      {device && (
        <div className="meta-line">
          Created {shortDate(device.createdAt)}
          {device.createdBy && <> by <b>{device.createdBy.name}</b></>}
          {device.enrolledAt && <> · Enrolled {longDate(device.enrolledAt)}</>}
        </div>
      )}

      {formError && <div className="kd-form-error"><AlertTriangle /><span>{formError}</span></div>}
    </KdModal>
  );
};

/* ====================================================================== *
 * Reactivate — three buttons, so not ConfirmModal
 * ====================================================================== */

const ReactivateModal: React.FC<{
  device: KioskDevice;
  onCancel: () => void;
  onReactivate: (thenCode: boolean) => Promise<void>;
}> = ({ device, onCancel, onReactivate }) => {
  const [busy, setBusy] = useState<null | 'plain' | 'code'>(null);
  const run = async (thenCode: boolean) => {
    setBusy(thenCode ? 'code' : 'plain');
    try { await onReactivate(thenCode); } finally { setBusy(null); }
  };
  const deactivated = device.deactivatedAt
    ? `Deactivated ${shortDate(device.deactivatedAt)}${device.deactivatedBy ? ` by ${device.deactivatedBy.name}` : ''}`
    : 'Deactivated';
  return (
    <KdModal
      Icon={Power}
      title={`Reactivate ${device.name}?`}
      sub={deactivated}
      wide
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="kd-btn left" onClick={onCancel} disabled={busy != null}>Cancel</button>
          <button type="button" className="kd-btn" onClick={() => run(false)} disabled={busy != null}>
            {busy === 'plain' ? 'Reactivating…' : 'Reactivate'}
          </button>
          <button type="button" className="kd-btn primary" autoFocus onClick={() => run(true)} disabled={busy != null}>
            <KeyRound />{busy === 'code' ? 'Working…' : 'Reactivate & generate code'}
          </button>
        </>
      )}
    >
      <p className="lead">
        Its old tablet was signed out when it was deactivated, so it comes back as <b>Not enrolled</b>.
        Generate a code to enroll a tablet.
      </p>
    </KdModal>
  );
};

/* ====================================================================== *
 * Enrollment code screen — active / expired / success
 * ====================================================================== */

const CodeModal: React.FC<{
  session: CodeSession;
  device: KioskDevice;
  now: number;
  adminName: string;
  generating: boolean;
  onNewCode: () => void;
  onClose: () => void;
}> = ({ session, device, now, adminName, generating, onNewCode, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(copyTimer.current), []);
  useEffect(() => { setCopied(false); }, [session.code.code]);

  const copy = async () => {
    try { await navigator.clipboard?.writeText(codeDigits(session.code.code)); } catch { /* clipboard blocked — the code is on screen */ }
    setCopied(true);
    window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
  };

  if (session.state === 'success') {
    return (
      <KdModal
        wide
        backdropCloses={false}
        onClose={onClose}
        footer={<button type="button" className="kd-btn primary" autoFocus onClick={onClose}><Check />Done</button>}
      >
        <div className="kd-success">
          <div className="big"><Check /></div>
          <h2>{device.name} enrolled <span className="tick">✓</span></h2>
          <p>
            The tablet is ready. Employees sign in on it with their own PIN, and every punch there is
            stamped <b>{device.name}</b>.
          </p>
          <div className="sfacts">
            <span><b>Just now</b>Enrolled</span>
            <span><b>{adminName}</b>Code generated by</span>
          </div>
        </div>
      </KdModal>
    );
  }

  const expired = session.state === 'expired';
  const [g1, g2] = codeGroups(session.code.code);
  const left = Math.max(0, session.deadline - now);
  const title = `${session.reenroll ? 'Re-enroll code' : 'Enrollment code'} · ${device.name}`;
  const expiredSub = session.expiredReason === 'replaced'
    ? 'A newer code was generated for this device, so this one no longer works.'
    : session.expiredReason === 'deactivated'
      ? 'This device was deactivated, so this code no longer works.'
      : `This code expired at ${clockTime(new Date(session.deadline).toISOString())} and was never used.`;

  return (
    <KdModal
      tone={expired ? 'warn' : ''}
      Icon={expired ? Clock : KeyRound}
      title={title}
      sub={expired ? expiredSub : 'Read this to the person at the tablet.'}
      wide
      backdropCloses={false}
      onClose={onClose}
      footer={expired ? (
        <>
          <button type="button" className="kd-btn" onClick={onClose}>Close</button>
          {session.expiredReason !== 'deactivated' && (
            <button type="button" className="kd-btn primary" autoFocus onClick={onNewCode} disabled={generating}>
              <KeyRound />{generating ? 'Generating…' : 'Generate new code'}
            </button>
          )}
        </>
      ) : (
        <>
          <button type="button" className="kd-btn ghost left" onClick={onNewCode} disabled={generating}>
            <RefreshCw />{generating ? 'Generating…' : 'Generate new code'}
          </button>
          <button type="button" className="kd-btn" onClick={onClose}>Close</button>
        </>
      )}
    >
      {!expired && session.replaced && (
        <div className="note-strip info">
          <Info /><span>The earlier code for this device no longer works — only the one below does.</span>
        </div>
      )}
      {!expired && session.reenroll && (
        <div className="note-strip warn">
          <LogOut /><span>When a tablet uses this code, <b>the tablet signed in as {device.name} now is signed out.</b></span>
        </div>
      )}

      <div className={`codebox${expired ? ' expired' : ''}`}>
        <div className="cl">{expired ? 'Expired code' : 'One-time code'}</div>
        <div className="code" aria-label={`Code ${(g1 + g2).split('').join(' ')}`}><span>{g1}</span><span>{g2}</span></div>
        {!expired && (
          <>
            <div className="say">Say it as <b>{spokenCode(session.code.code)}</b></div>
            <button type="button" className="kd-btn sm copy" onClick={copy}>
              {copied ? <><Check />Copied</> : <><Copy />Copy code</>}
            </button>
          </>
        )}
      </div>

      {expired ? (
        session.expiredReason === 'deactivated' ? null : (
          <p className="exp-msg">
            Nobody enrolled with it. Generate a new code when you're on the phone with the person at the
            tablet — it's good for another 15 minutes.
          </p>
        )
      ) : (
        <>
          <div className={`timer${left < CODE_LOW_MS ? ' low' : ''}`}>
            <div className="bar"><i style={{ width: `${(left / session.ttl) * 100}%` }} /></div>
            <span className="tl">Expires in <b>{mmss(left)}</b></span>
          </div>
          <div className="waiting"><span className="pulse" />Waiting for the tablet — this window updates on its own when it enrolls.</div>
          <ol className="steps">
            <li className="sh"><Tablet />At the tablet</li>
            <li className="s"><span className="n">1</span><span>Open the <b>Task &amp; Time Kiosk</b> app</span></li>
            <li className="s"><span className="n">2</span><span>Enter the company ID: <b className="mono">{device.companyID}</b></span></li>
            <li className="s"><span className="n">3</span><span>Tap <b>Enroll this kiosk</b></span></li>
            <li className="s"><span className="n">4</span><span>Enter the code</span></li>
          </ol>
          <div className="rules">
            <div className="r"><EyeOff /><span><b>Shown only once.</b> If you close this window, you'll need a new code.</span></div>
            <div className="r"><RefreshCw /><span><b>Generating a new code cancels this one.</b></span></div>
          </div>
        </>
      )}
    </KdModal>
  );
};

export default KioskDevicesPage;
